import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] focus:ring-1 focus:ring-[#00FF66]/40 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("De wachtwoorden komen niet overeen");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <header className="border-b border-slate-800 bg-[#090D16]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <a href="/" data-testid="reset-logo-link"><Logo compact /></a>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 sm:px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <h1 className="font-display text-5xl uppercase tracking-wide text-white">Ongeldige link</h1>
              <p className="mt-4 text-slate-400">Deze resetlink mist een token. Vraag een nieuwe aan via de inlogpagina.</p>
              <button onClick={() => navigate("/account")} data-testid="reset-to-login"
                className="mt-8 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase px-7 py-3.5 rounded-full glow-lime">
                Naar inloggen
              </button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="mx-auto w-20 h-20 rounded-full bg-[#00FF66]/15 border-2 border-[#00FF66] flex items-center justify-center glow-lime mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
              </div>
              <h1 className="font-display text-5xl uppercase tracking-wide text-white">Wachtwoord aangepast</h1>
              <p className="mt-4 text-slate-400">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
              <button onClick={() => navigate("/account")} data-testid="reset-success-login"
                className="mt-8 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase px-7 py-3.5 rounded-full glow-lime">
                Naar inloggen
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7 space-y-4" data-testid="reset-form">
              <h1 className="font-display text-4xl uppercase tracking-wide text-white">Nieuw wachtwoord</h1>
              <p className="text-sm text-slate-400">Kies een nieuw wachtwoord voor je Gelvaro-account.</p>
              <label className="block">
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Nieuw wachtwoord</span>
                <input data-testid="reset-password-input" required type="password" minLength={6} className={inputClass}
                  placeholder="Minimaal 6 tekens" value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Herhaal wachtwoord</span>
                <input data-testid="reset-confirm-input" required type="password" minLength={6} className={inputClass}
                  placeholder="Nogmaals" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </label>
              {error && <p className="text-sm text-[#FF087B] font-semibold" data-testid="reset-error">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit-button"
                disabled={busy}
                className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-4 rounded-full glow-lime disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Wachtwoord opslaan
              </button>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
}
